import { useState, useRef } from "react";
import { FileImage, Loader2, ChevronDown } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { extractSyncBolS3Error, formatSyncBolS3Error } from "@/lib/sync-bol-s3-error";
import { deleteOrderDocument } from "@/lib/order-doc-delete";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { cn } from "@/lib/utils";
import { BolUploader } from "@/components/orders/BolUploader";
import { DocumentDetailDialog } from "@/components/documents/DocumentDetailDialog";
import { ZoomableImage } from "@/components/ui/ZoomableImage";
import type { ExtractionRow } from "@/components/documents/DocumentHistory";
import { StatusBadge } from "@/components/documents/DocumentHistory";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  FileText, AlertCircle, Download, RefreshCw, Trash2,
} from "lucide-react";

interface BolViewerDialogProps {
  orderId: string;
  orderUniqueId: string;
}

export function BolDocumentPreview({ filePath, bucket = "bol-images" }: { filePath: string; bucket?: string }) {
  const { data: urls, isLoading: loading } = useQuery({
    queryKey: ["doc-preview-url", bucket, filePath],
    queryFn: async () => {
      // Try the specified bucket first, fall back to idp-uploads
      for (const b of [bucket, "idp-uploads"]) {
        const { data, error } = await supabase.storage
          .from(b)
          .createSignedUrl(filePath, 3600);
        if (!error && data?.signedUrl) {
          let blobUrl: string | null = null;
          try {
            const resp = await fetch(data.signedUrl);
            if (resp.ok) {
              const blob = await resp.blob();
              blobUrl = URL.createObjectURL(blob);
              return { signedUrl: data.signedUrl, blobUrl };
            }
          } catch { /* ignore */ }
          return { signedUrl: data.signedUrl, blobUrl };
        }
      }
      return null;
    },
    staleTime: 1000 * 60 * 5,
  });

  const signedUrl = urls?.signedUrl ?? null;
  const blobUrl = urls?.blobUrl ?? null;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 bg-muted/30 rounded-lg">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!blobUrl && !signedUrl) {
    return (
      <div className="flex items-center justify-center py-8 bg-muted/30 rounded-lg text-sm text-muted-foreground">
        Unable to load preview
      </div>
    );
  }

  const isPdf = filePath.toLowerCase().endsWith(".pdf");
  const isImage = /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(filePath);

  if (isPdf) {
    const viewUrl = signedUrl || blobUrl;
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-4 bg-muted/30 rounded-lg border">
        <FileImage className="h-16 w-16 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">PDF preview is not supported in this view</p>
        <div className="flex gap-2">
          <Button variant="default" size="sm" asChild>
            <a href={viewUrl!} target="_blank" rel="noopener noreferrer">
              <FileText className="h-4 w-4 mr-2" /> View PDF
            </a>
          </Button>
          {blobUrl && (
            <Button variant="outline" size="sm" asChild>
              <a href={blobUrl} download={filePath.split("/").pop()}>
                <Download className="h-4 w-4 mr-2" /> Download
              </a>
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (isImage && blobUrl) {
    return (
      <ZoomableImage
        src={blobUrl}
        openUrl={signedUrl || blobUrl}
        downloadUrl={blobUrl || signedUrl || undefined}
        fileName={filePath.split("/").pop() || "document"}
        alt="Document preview"
        className="rounded-lg border bg-muted/30 h-[50vh]"
      />
    );
  }

  return (
    <div className="flex flex-col items-center justify-center py-8 gap-3 bg-muted/30 rounded-lg">
      <FileText className="h-12 w-12 text-muted-foreground" />
      {blobUrl && (
        <Button asChild variant="outline" size="sm">
          <a href={blobUrl} download>
            <Download className="h-4 w-4 mr-2" /> Download to View
          </a>
        </Button>
      )}
    </div>
  );
}

const BOL_LABELS: Record<string, string> = {
  bol_number: "BOL Number",
  trailer_id: "Trailer ID",
  timestamp: "Timestamp",
  shipper_name: "Shipper",
  shipper_address: "Shipper Address",
  consignee_name: "Consignee",
  consignee_address: "Consignee Address",
  carrier_name: "Carrier",
  seal_number: "Seal #",
  po_number: "PO Number",
  weight: "Weight",
  pieces: "Pieces",
  commodity_description: "Commodity",
  special_instructions: "Special Instructions",
};

function BolDocumentActions({
  bolId,
  orderId,
  orderUniqueId,
  filePath,
  sourceTrailerId,
}: {
  bolId: string;
  orderId: string;
  orderUniqueId: string;
  filePath?: string | null;
  sourceTrailerId?: string | null;
}) {
  const queryClient = useQueryClient();
  const { activeTenant, tenantId } = useTenant();
  const fileRef = useRef<HTMLInputElement>(null);
  const [replacing, setReplacing] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [trailerInfoOpen, setTrailerInfoOpen] = useState(false);

  // Look up the trailer's human-readable text id when this BoL is trailer-sourced.
  const { data: sourceTrailerText } = useQuery({
    queryKey: ["trailer-text-id", sourceTrailerId, tenantId],
    queryFn: async () => {
      if (!sourceTrailerId) return null;
      const { data } = await supabase
        .from("trailers")
        .select("trailer_id")
        .eq("id", sourceTrailerId)
        .eq("tenant_id", tenantId!)
        .maybeSingle();
      return data?.trailer_id ?? null;
    },
    enabled: !!sourceTrailerId && !!tenantId,
    staleTime: 60_000,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["bol-images", orderId] });
    queryClient.invalidateQueries({ queryKey: ["bol-images-count", orderId] });
    queryClient.invalidateQueries({ queryKey: ["bol-extractions", orderId] });
  };

  const handleReplace = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setReplacing(true);
    let s3KeyForRollback: string | null = null;
    let newStoragePath: string | null = null;
    try {
      if (!tenantId) throw new Error("Tenant context is not ready");

      // Sync-then-Persist: push the replacement BoL to the NetSuite-facing
      // S3 bucket BEFORE updating storage / DB. Failure aborts the replace.
      const reader = new FileReader();
      const base64 = await new Promise<string>((resolve, reject) => {
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.split(",")[1] || result);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const { data: syncResult, error: syncError } = await supabase.functions.invoke("sync-bol-s3", {
        body: {
          file_data: base64,
          file_name: file.name,
          tenant_slug: activeTenant,
          order_id: orderUniqueId,
        },
      });
      if (syncError || !syncResult?.success) {
        const detail = await extractSyncBolS3Error(syncError, syncResult);
        throw new Error(`S3 upload failed: ${formatSyncBolS3Error(detail)}`);
      }
      s3KeyForRollback = syncResult?.s3_key ?? null;

      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const path = `bol/${Date.now()}-${safeName}`;
      const { error: upErr } = await supabase.storage.from("bol-images").upload(path, file);
      if (upErr) throw upErr;
      newStoragePath = path;
      const { error: updErr } = await supabase.rpc("replace_order_bol_image" as any, {
        _bol_id: bolId,
        _tenant_id: tenantId,
        _image_path: path,
        _image_url: path,
      });
      if (updErr) throw updErr;

      // Persist the new S3 key + original file name so a later delete can find
      // the object (the RPC above only swaps the storage path).
      await supabase
        .from("order_bol_images")
        .update({ s3_key: s3KeyForRollback, file_name: file.name } as never)
        .eq("id", bolId)
        .eq("tenant_id", tenantId);

      // Step 4: Strict cleanup of OLD S3 object(s) and storage file for the
      // previous image_path so the order ends up with exactly one BoL file.
      if (filePath) {
        const fileName = filePath.split("/").pop()?.replace(/^\d+-/, "") || "";
        const sourcePaths = Array.from(new Set([filePath, fileName].filter(Boolean)));
        const { data: syncRows } = await supabase
          .from("s3_sync_log")
          .select("s3_key")
          .eq("order_id", orderUniqueId)
          .eq("status", "synced")
          .in("source_path", sourcePaths.length > 0 ? sourcePaths : ["__none__"]);
        const oldKeys = Array.from(new Set((syncRows ?? []).map((r: any) => r.s3_key as string)))
          .filter((k) => k !== s3KeyForRollback);
        for (const key of oldKeys) {
          const { data, error } = await supabase.functions.invoke("sync-bol-s3", {
            body: {
              action: "delete",
              tenant_slug: activeTenant,
              order_id: orderUniqueId,
              s3_key: key,
            },
          });
          if (error || (data && data.success === false)) {
            throw new Error(error?.message || data?.error || `S3 delete failed for old key ${key}`);
          }
        }
        // Best-effort: drop the previous object from local storage too.
        await supabase.storage.from("bol-images").remove([filePath]).catch(() => undefined);

        // Best-effort: drop stale document_extractions row(s) for the previous file.
        try {
          const orConditions: string[] = [];
          if (filePath) orConditions.push(`file_path.eq.${filePath}`);
          if (fileName) orConditions.push(`file_name.eq.${fileName}`);
          if (orConditions.length > 0) {
            await supabase
              .from("document_extractions")
              .delete()
              .eq("tenant_id", tenantId)
              .or(orConditions.join(","));
          }
        } catch (extractionErr) {
          console.warn("Failed to clean prior BoL document_extractions row", extractionErr);
        }
      }

      toast.success("File replaced");
      invalidate();
    } catch (err: any) {
      if (s3KeyForRollback) {
        try {
          await supabase.functions.invoke("sync-bol-s3", {
            body: {
              action: "delete",
              tenant_slug: activeTenant,
              order_id: orderUniqueId,
              s3_key: s3KeyForRollback,
            },
          });
        } catch (cleanupErr) {
          console.error("S3 rollback failed for key", s3KeyForRollback, cleanupErr);
          toast.error(
            `S3 cleanup failed — manual review needed for s3_key=${s3KeyForRollback}`
          );
        }
      }
      if (newStoragePath) {
        await supabase.storage.from("bol-images").remove([newStoragePath]).catch(() => undefined);
      }
      toast.error("Replace failed: " + (err.message || "Unknown error"));
    } finally {
      setReplacing(false);
    }
  };

  const handleRemove = async () => {
    if (!tenantId) {
      toast.error("Tenant context is not ready");
      return;
    }
    setRemoving(true);
    try {
      await deleteOrderDocument({
        table: "order_bol_images",
        bucket: "bol-images",
        rowId: bolId,
        imagePath: filePath,
        orderUniqueId,
        tenantId,
        tenantSlug: activeTenant,
      });
      toast.success("BoL removed from order");
      invalidate();
    } catch (err: any) {
      toast.error("Remove failed: " + (err.message || "Unknown error"));
    } finally {
      setRemoving(false);
    }
  };

  const isTrailerSourced = !!sourceTrailerId;

  return (
    <div className="flex items-center gap-2">
      <input ref={fileRef} type="file" className="hidden" accept="image/*,.pdf" onChange={handleReplace} />
      <Button
        variant="outline"
        size="sm"
        className="gap-1.5"
        disabled={replacing || isTrailerSourced}
        onClick={() => fileRef.current?.click()}
      >
        {replacing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
        Replace
      </Button>
      {isTrailerSourced ? (
        <>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 text-destructive hover:text-destructive"
            onClick={() => setTrailerInfoOpen(true)}
          >
            <Trash2 className="h-3.5 w-3.5" />
            Remove
          </Button>
          <AlertDialog open={trailerInfoOpen} onOpenChange={setTrailerInfoOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Trailer-sourced BoL</AlertDialogTitle>
                <AlertDialogDescription>
                  This BoL is associated with trailer{" "}
                  <span className="font-semibold text-foreground">
                    {sourceTrailerText ?? "—"}
                  </span>
                  . Edit the trailer in the order to remove it.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogAction onClick={() => setTrailerInfoOpen(false)}>
                  OK
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      ) : (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 text-destructive hover:text-destructive"
              disabled={removing}
            >
              {removing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
              Remove
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove BoL?</AlertDialogTitle>
              <AlertDialogDescription>
                This will delete the document from S3 and remove it from this order. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleRemove}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Remove
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}

function BolExistingView({ orderId, orderUniqueId }: { orderId: string; orderUniqueId: string }) {
  const [selectedRow, setSelectedRow] = useState<ExtractionRow | null>(null);

  const { data: bolImages, isLoading } = useQuery({
    queryKey: ["bol-images", orderId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("order_bol_images")
        .select("*")
        .eq("order_id", orderId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  const bolPaths = (bolImages || []).map((b) => b.image_path).filter(Boolean) as string[];
  const bolFileNames = bolPaths.map((p) => p.split("/").pop()?.replace(/^\d+-/, "") || "").filter(Boolean);

  const { data: extractions } = useQuery({
    queryKey: ["bol-extractions", orderId, bolPaths, bolFileNames],
    queryFn: async () => {
      if (bolPaths.length === 0) return [];
      const conditions: string[] = [];
      if (bolPaths.length > 0) conditions.push(`file_path.in.(${bolPaths.join(",")})`);
      if (bolFileNames.length > 0) conditions.push(`file_name.in.(${bolFileNames.join(",")})`);
      const { data, error } = await supabase
        .from("document_extractions")
        .select("*")
        .or(conditions.join(","));
      if (error) throw error;
      return (data || []) as ExtractionRow[];
    },
    enabled: bolPaths.length > 0,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const extractionMap = new Map<string, ExtractionRow>();
  (extractions || []).forEach((e) => {
    extractionMap.set(e.file_path, e);
    if (e.file_name) extractionMap.set(e.file_name, e);
  });

  const findExtraction = (imagePath: string | null): ExtractionRow | null => {
    if (!imagePath) return null;
    const direct = extractionMap.get(imagePath);
    if (direct) return direct;
    const fileName = imagePath.split("/").pop()?.replace(/^\d+-/, "") || "";
    return extractionMap.get(fileName) || null;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          {(bolImages || []).length} document{(bolImages || []).length !== 1 ? "s" : ""}
        </p>
        <BolUploader
          orderId={orderId}
          orderUniqueId={orderUniqueId}
          mode="dispatcher"
          variant="compact"
        />
      </div>
      {(bolImages || []).map((bol) => {
        const extraction = findExtraction(bol.image_path);

        if (extraction) {
          const displayData = extraction.verified_data || extraction.extracted_data || {};
          const confidence = (displayData as any).confidence_score ?? 0;

          return (
            <div key={bol.id} className="space-y-4">
              <p className="text-xs font-medium text-foreground truncate">{extraction.file_name}</p>
              <div className="flex items-center gap-3 flex-wrap text-sm text-muted-foreground">
                <StatusBadge status={extraction.status} isVerified={extraction.is_verified} isAttached />
                <span className="ml-auto">Uploaded {format(new Date(extraction.created_at), "MMM d, yyyy h:mm a")}</span>
              </div>

              {extraction.file_path && (
                <div className="relative">
                  <BolDocumentPreview filePath={extraction.file_path} bucket="bol-images" />
                  <div className="absolute top-2 right-2 z-10">
                    <BolDocumentActions
                      bolId={bol.id}
                      orderId={orderId}
                      orderUniqueId={orderUniqueId}
                      filePath={bol.image_path}
                      sourceTrailerId={(bol as any).source_trailer_id ?? null}
                    />
                  </div>
                </div>
              )}

              {extraction.error_message && (
                <div className="bg-destructive/10 text-destructive text-sm rounded-lg p-3 flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  {extraction.error_message}
                </div>
              )}

              {false && Object.keys(displayData).length > 0 && (
                <Collapsible>
                  <div className="border rounded-lg p-4">
                    <CollapsibleTrigger className="flex w-full items-center justify-between">
                      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        Extracted Data
                      </h4>
                      <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200 [[data-state=open]>&]:rotate-180" />
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <dl className="space-y-2 mt-3">
                        {Object.entries(BOL_LABELS).map(([key, label]) => {
                          const val = (displayData as any)[key];
                          if (val === null || val === undefined || val === "") return null;
                          return (
                            <div key={key} className="flex justify-between gap-4 py-1.5 border-b border-border/50 last:border-0">
                              <dt className="text-sm text-muted-foreground shrink-0">{label}</dt>
                              <dd className="text-sm font-medium text-foreground text-right">{String(val)}</dd>
                            </div>
                          );
                        })}
                        {confidence > 0 && (
                          <div className="flex justify-between gap-4 py-1.5">
                            <dt className="text-sm text-muted-foreground">Confidence</dt>
                            <dd className={cn(
                              "text-sm font-semibold",
                              confidence >= 0.9 ? "text-primary" : confidence >= 0.7 ? "text-amber-600" : "text-destructive"
                            )}>
                              {Math.round(confidence * 100)}%
                            </dd>
                          </div>
                        )}
                      </dl>
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              )}
            </div>
          );
        }

        // No extraction found — show preview with status chips
        return (
          <div key={bol.id} className="space-y-4">
            <div className="flex items-center gap-3 flex-wrap text-sm text-muted-foreground">
              <Badge variant="outline" className="gap-1">
                <FileImage className="h-3 w-3" /> BOL
              </Badge>
              <StatusBadge status="completed" isVerified={false} isAttached />
              <span className="ml-auto">Uploaded {format(new Date(bol.created_at), "MMM d, yyyy h:mm a")}</span>
            </div>

            <div className="relative">
              {bol.image_path ? (
                <BolDocumentPreview filePath={bol.image_path} bucket="bol-images" />
              ) : bol.image_url ? (
                <div className="rounded-lg overflow-hidden border bg-muted/30">
                  <img src={bol.image_url} alt="Bill of Lading" className="w-full h-auto max-h-[50vh] object-contain mx-auto" />
                </div>
              ) : null}
              <div className="absolute top-2 right-2 z-10">
                <BolDocumentActions
                  bolId={bol.id}
                  orderId={orderId}
                  orderUniqueId={orderUniqueId}
                  filePath={bol.image_path}
                  sourceTrailerId={(bol as any).source_trailer_id ?? null}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function BolViewerDialog({ orderId, orderUniqueId }: BolViewerDialogProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Light query to check if BoL exists (always enabled for icon styling)
  const { data: bolCount } = useQuery({
    queryKey: ["bol-images-count", orderId],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("order_bol_images")
        .select("id", { count: "exact", head: true })
        .eq("order_id", orderId);

      if (error) throw error;
      return count || 0;
    },
  });

  const hasBolImages = (bolCount ?? 0) > 0;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <DialogTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <FileImage
                className={cn(
                  "h-4 w-4",
                  hasBolImages ? "text-foreground" : "text-muted-foreground/40"
                )}
              />
            </Button>
          </DialogTrigger>
        </TooltipTrigger>
        <TooltipContent>
          {hasBolImages ? "View Bill of Lading" : "No Bill of Lading"}
        </TooltipContent>
      </Tooltip>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="pr-8">
          <DialogTitle>
            {hasBolImages
              ? `Bill of Lading for ${orderUniqueId}`
              : `Add Bill of Lading to ${orderUniqueId}`}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-auto pt-2">
          {hasBolImages ? (
            <BolExistingView orderId={orderId} orderUniqueId={orderUniqueId} />
          ) : (
            <BolUploader
              orderId={orderId}
              orderUniqueId={orderUniqueId}
              mode="dispatcher"
              variant="dropzone"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
